import { useEffect, useRef, useState } from "react"

import { scanCopy } from "../config/copy"
import { CameraMaskOverlay } from "./camera-mask-overlay"
import { ScanNavbar } from "./scan-navbar"

interface PalmCameraProps {
  onBack: () => void
  onCaptured: (dataUrl: string) => void
  /** Camera couldn't start (permission denied / unavailable). Falls back to onBack. */
  onError?: () => void
  /** Which palm is being captured — drives the navbar title. Defaults to left. */
  hand?: "left" | "right"
}

/** Live camera view with a pill-shaped mask and round shutter button. */
export function PalmCamera({ onBack, onCaptured, onError, hand = "left" }: PalmCameraProps) {
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)
  const onBackRef = useRef(onBack)
  const onErrorRef = useRef(onError)
  // Keep the preview hidden until the camera's framing has settled (see below).
  const [ready, setReady] = useState(false)

  useEffect(() => {
    onBackRef.current = onBack
    onErrorRef.current = onError
  })

  // The back camera reports one frame size on start, then updates it a beat later
  // (iOS fires a `resize` on the <video>; multi-lens phones re-crop from a wide field
  // of view). With object-cover that shift snaps the framing from "far" to "near".
  // Reveal the preview only once those size changes go quiet, so it appears already at
  // its final crop instead of visibly zooming in. 450ms after the last change is the
  // settle window proven on device by the selfie camera; each `resize` re-arms it.
  useEffect(() => {
    const video = videoRef.current
    if (!video) return
    let revealTimer: ReturnType<typeof setTimeout> | null = null
    const armReveal = () => {
      if (revealTimer) clearTimeout(revealTimer)
      revealTimer = setTimeout(() => setReady(true), 450)
    }
    video.addEventListener("playing", armReveal)
    video.addEventListener("resize", armReveal)
    // Safety: never leave the preview hidden if `playing` never arrives (odd WebViews).
    const safety = setTimeout(() => setReady(true), 2500)
    return () => {
      video.removeEventListener("playing", armReveal)
      video.removeEventListener("resize", armReveal)
      if (revealTimer) clearTimeout(revealTimer)
      clearTimeout(safety)
    }
  }, [])

  const stopCamera = () => {
    streamRef.current?.getTracks().forEach((track) => track.stop())
    streamRef.current = null
  }

  useEffect(() => {
    let cancelled = false

    const start = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "environment" },
        })
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop())
          return
        }
        streamRef.current = stream
        if (videoRef.current) videoRef.current.srcObject = stream
      } catch {
        if (cancelled) return
        if (onErrorRef.current) onErrorRef.current()
        else onBackRef.current()
      }
    }

    void start()
    return () => {
      cancelled = true
      stopCamera()
    }
  }, [])

  const capture = () => {
    const video = videoRef.current
    if (!video) return
    const canvas = document.createElement("canvas")
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const ctx = canvas.getContext("2d")
    if (!ctx) return
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
    stopCamera()
    onCaptured(canvas.toDataURL("image/jpeg", 0.9))
  }

  return (
    <div className="fixed inset-0 z-[1000] mx-auto w-full max-w-app bg-black">
      {/* biome-ignore lint/a11y/useMediaCaption: live camera preview */}
      <video
        ref={videoRef}
        autoPlay
        playsInline
        muted
        className="absolute inset-0 size-full object-cover transition-opacity duration-300"
        style={{ opacity: ready ? 1 : 0 }}
      />

      {!ready && (
        <div className="pointer-events-none absolute inset-0 z-[5] flex items-center justify-center">
          <p className="font-body text-sm text-purple-light/70">{scanCopy.camera.starting}</p>
        </div>
      )}

      <CameraMaskOverlay />

      <div className="relative z-10 flex h-full flex-col">
        <ScanNavbar
          onBack={() => {
            stopCamera()
            onBack()
          }}
          title={scanCopy.camera.title(hand)}
          subtitle={scanCopy.camera.subtitle}
        />

        <div className="flex-1" />

        <div className="flex justify-center pb-6">
          <button
            type="button"
            onClick={capture}
            disabled={!ready}
            aria-label={scanCopy.camera.capture}
            className="relative size-16 rounded-full border-2 border-purple-accent transition-opacity disabled:opacity-40"
          >
            <span className="absolute inset-[18.75%] rounded-full bg-white" />
          </button>
        </div>
      </div>
    </div>
  )
}
